import { db } from '../firebase';
import {
    collection, doc, getDoc, setDoc, updateDoc, deleteField, query, getDocs, deleteDoc, FieldPath
} from 'firebase/firestore';

const COLLECTION_LIBRARY = 'library';

export const libraryService = {
    /**
     * Helper to fetch raw library document from a specific location (or "global").
     */
    async _getLibraryDoc(pathSegment = 'global') {
        try {
            const docRef = doc(db, COLLECTION_LIBRARY, pathSegment);
            const docSnap = await getDoc(docRef);
            return docSnap.exists() ? docSnap.data() : null;
        } catch (error) {
            console.error(`Error fetching library doc '${pathSegment}':`, error);
            return null;
        }
    },

    async getGlobalLibrary() {
        try {
            const dbData = await this._getLibraryDoc('global') || {};

            const finalLibrary = dbData.onderdeel_tasks || {};
            const finalMetadata = dbData.task_metadata || {};
            const finalConfig = dbData.template_config || null;
            const finalTools = dbData.global_tools || [];
            const finalPPEs = dbData.global_ppes || [];

            let dbDisciplines = dbData.config_disciplines;
            if (!dbDisciplines || dbDisciplines.length === 0) {
                dbDisciplines = [
                    "Werktuigbouwkunde", "Elektrotechniek", "Mechatronisch",
                    "Civiel", "Procestechniek", "Anders"
                ];
            }

            let dbWaste = dbData.config_waste;
            if (!dbWaste || dbWaste.length === 0) {
                dbWaste = [
                    { id: 'restafval', label: 'Restafval', color: 'bg-slate-500' },
                    { id: 'papier', label: 'Papier/Karton', color: 'bg-blue-500' },
                    { id: 'hout', label: 'Hout', color: 'bg-orange-500' },
                    { id: 'metaal', label: 'Metaal', color: 'bg-red-500' },
                    { id: 'kga', label: 'KGA', color: 'bg-red-500' },
                    { id: 'puin', label: 'Puin', color: 'bg-slate-500' }
                ];
            }

            const finalChecks = (dbData.mechanic_checks_library && !Array.isArray(dbData.mechanic_checks_library))
                ? dbData.mechanic_checks_library
                : {};

            const sourceMap = {};
            if (finalLibrary) {
                Object.keys(finalLibrary).forEach(k => sourceMap[k] = 'global');
            }

            return {
                onderdeel_tasks: finalLibrary,
                task_metadata: finalMetadata,
                template_config: finalConfig,
                global_tools: finalTools,
                global_ppes: finalPPEs,
                config_disciplines: dbDisciplines,
                config_waste: dbWaste,
                mechanic_checks_library: finalChecks,
                iso_14224_library: dbData.iso_14224_library || {},
                system_settings: dbData.system_settings || {},
                _sourceMap: sourceMap
            };
        } catch (error) {
            console.error('[LibraryService] getGlobalLibrary failed:', error);
            throw error;
        }
    },

    async getLocationLibrary(locationName) {
        if (!locationName) return null;
        try {
            return await this._getLibraryDoc(locationName);
        } catch (error) {
            console.error(`[LibraryService] getLocationLibrary failed for '${locationName}':`, error);
            throw error;
        }
    },

    async getCombinedLibrary(locationName) {
        try {
            const globalData = await this.getGlobalLibrary();

            if (!locationName) return globalData;

            const localData = await this.getLocationLibrary(locationName);

            if (!localData) {
                return globalData;
            }

            // Merge Logic - STRICT GLOBAL PRIORITY for Tasks, UNION for others
            const mergedLibrary = { ...globalData.onderdeel_tasks };
            if (localData.onderdeel_tasks) {
                Object.keys(localData.onderdeel_tasks).forEach(taskName => {
                    if (!mergedLibrary[taskName]) {
                        mergedLibrary[taskName] = localData.onderdeel_tasks[taskName];
                    }
                });
            }

            const mergedMetadata = { ...globalData.task_metadata };
            if (localData.task_metadata) {
                Object.keys(localData.task_metadata).forEach(taskName => {
                    if (!mergedMetadata[taskName]) {
                        mergedMetadata[taskName] = localData.task_metadata[taskName];
                    }
                });
            }

            const uniqueById = (arr1, arr2) => {
                const map = new Map();
                (arr1 || []).forEach(i => map.set(i.id || i.value || i, i));
                (arr2 || []).forEach(i => map.set(i.id || i.value || i, i));
                return Array.from(map.values());
            };
            const uniqueStrings = (arr1, arr2) => [...new Set([...(arr1 || []), ...(arr2 || [])])];

            const mergedTools = uniqueById(globalData.global_tools, localData.global_tools);
            const mergedPPEs = uniqueById(globalData.global_ppes, localData.global_ppes);
            const mergedDisciplines = uniqueStrings(globalData.config_disciplines, localData.config_disciplines);
            const mergedWaste = uniqueById(globalData.config_waste, localData.config_waste);

            const mergeCheckLibraries = (globalLib, localLib) => {
                if (!globalLib && !localLib) return {};
                const base = (globalLib && !Array.isArray(globalLib)) ? { ...globalLib } : {};
                const override = (localLib && !Array.isArray(localLib)) ? localLib : {};

                Object.keys(override).forEach(cat => {
                    if (base[cat]) {
                        base[cat] = uniqueStrings(base[cat], override[cat]);
                    } else {
                        base[cat] = override[cat];
                    }
                });
                return base;
            };

            const mergedChecks = mergeCheckLibraries(globalData.mechanic_checks_library, localData.mechanic_checks_library);
            const mergedConfig = localData.template_config || globalData.template_config;

            // GENERATE SOURCE MAP
            const sourceMap = {};
            if (globalData.onderdeel_tasks) {
                Object.keys(globalData.onderdeel_tasks).forEach(k => sourceMap[k] = 'global');
            }
            if (localData.onderdeel_tasks) {
                Object.keys(localData.onderdeel_tasks).forEach(k => {
                    if (sourceMap[k]) sourceMap[k] = 'local_override';
                    else sourceMap[k] = 'local_only';
                });
            }

            return {
                ...globalData,
                onderdeel_tasks: mergedLibrary,
                task_metadata: mergedMetadata,
                template_config: mergedConfig,
                global_tools: mergedTools,
                global_ppes: mergedPPEs,
                config_disciplines: mergedDisciplines,
                config_waste: mergedWaste,
                mechanic_checks_library: mergedChecks,
                _isCombined: true,
                _contextLocation: locationName,
                _sourceMap: sourceMap
            };
        } catch (error) {
            console.error(`[LibraryService] getCombinedLibrary failed for '${locationName}':`, error);
            throw error;
        }
    },

    // --- SAVERS ---

    // Helper for safe saving (removing undefineds)
    async _saveLibraryDoc(pathSegment, data) {
        try {
            const docRef = doc(db, COLLECTION_LIBRARY, pathSegment);

            const sanitize = (input) => {
                if (input === undefined) return undefined;
                if (input === null) return null;
                if (typeof input !== 'object') return input;
                if (Array.isArray(input)) return input.map(sanitize);
                if (Object.getPrototypeOf(input) !== Object.prototype) return input;

                const output = {};
                for (const key in input) {
                    const value = input[key];
                    if (value !== undefined) {
                        const cleaned = sanitize(value);
                        if (cleaned !== undefined) {
                            output[key] = cleaned;
                        }
                    }
                }
                return output;
            };

            const cleanPayload = sanitize(data);
            await setDoc(docRef, cleanPayload, { merge: true });
        } catch (error) {
            console.error(`[LibraryService] _saveLibraryDoc failed for '${pathSegment}':`, error);
            throw error;
        }
    },

    async saveGlobalLibrary(tasks, metadata, config, tools, ppes, mechanicChecks, disciplines, waste) {
        try {
            const data = {};
            if (tasks) data.onderdeel_tasks = tasks;
            if (metadata) data.task_metadata = metadata;
            if (config) data.template_config = config;
            if (tools) data.global_tools = tools;
            if (ppes) data.global_ppes = ppes;
            if (mechanicChecks) data.mechanic_checks_library = mechanicChecks;
            if (disciplines) data.config_disciplines = disciplines;
            if (waste) data.config_waste = waste;
            data.updatedAt = new Date().toISOString();

            await this._saveLibraryDoc('global', data);
        } catch (error) {
            console.error('[LibraryService] saveGlobalLibrary failed:', error);
            throw error;
        }
    },

    async saveSystemSettings(settings) {
        try {
            await this._saveLibraryDoc('global', { system_settings: settings, updatedAt: new Date().toISOString() });
        } catch (error) {
            console.error('[LibraryService] saveSystemSettings failed:', error);
            throw error;
        }
    },

    async saveLocationLibrary(locationName, data) {
        if (!locationName) throw new Error("No location name provided for saveLocationLibrary");
        try {
            await this._saveLibraryDoc(locationName, data);
        } catch (error) {
            console.error(`[LibraryService] saveLocationLibrary failed for '${locationName}':`, error);
            throw error;
        }
    },

    async updateLibraryTask(taskName, taskData, metadata, locationName = null) {
        try {
            const targetDoc = locationName || 'global';

            const docRef = doc(db, COLLECTION_LIBRARY, targetDoc);
            const updates = [];

            if (taskData) {
                updates.push(new FieldPath('onderdeel_tasks', taskName));
                updates.push(taskData);
            }
            if (metadata) {
                const cleanMetadata = Object.fromEntries(
                    Object.entries(metadata).filter(([_, v]) => v !== undefined)
                );
                updates.push(new FieldPath('task_metadata', taskName));
                updates.push(cleanMetadata);
            }

            if (updates.length > 0) {
                await updateDoc(docRef, ...updates);
            }
        } catch (error) {
            console.error(`[LibraryService] updateLibraryTask failed for '${taskName}':`, error);
            throw error;
        }
    },

    async deleteLibraryTask(taskName, locationName = null) {
        const targetDoc = locationName || 'global';
        const docRef = doc(db, COLLECTION_LIBRARY, targetDoc);
        try {
            await updateDoc(docRef, {
                [`onderdeel_tasks.${taskName}`]: deleteField(),
                [`task_metadata.${taskName}`]: deleteField()
            });
        } catch (e) {
            console.warn(`Error deleting task:`, e);
        }
    },

    async resetLocationLibrary(locationName) {
        if (!locationName) return;
        const docRef = doc(db, COLLECTION_LIBRARY, locationName);
        try {
            await updateDoc(docRef, {
                onderdeel_tasks: deleteField(),
                task_metadata: deleteField()
            });
        } catch (e) {
            console.warn("Reset failed:", e);
        }
    },

    // --- Task Image Persistence ---

    async getAllTaskImagesRaw() {
        try {
            const q = query(collection(db, 'task_images'));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('[LibraryService] getAllTaskImagesRaw failed:', error);
            throw error;
        }
    },

    async deleteTaskImage(docId) {
        try {
            await deleteDoc(doc(db, 'task_images', docId));
        } catch (error) {
            console.error('[LibraryService] deleteTaskImage failed:', error);
            throw error;
        }
    },

    // De locatie hoort als veld in het document, niet alleen in de id. De id is
    // opgebouwd als locatie_hal_lijn_..._taak met underscores, en omdat een
    // locatienaam zelf ook underscores krijgt is dat niet betrouwbaar te
    // ontleden. De beveiligingsregels hebben een echt veld nodig.
    async saveTaskImages(pathId, urls, locationId) {
        try {
            if (!locationId) {
                throw new Error(
                    'saveTaskImages zonder locatie: de foto zou dan bij geen enkele klant horen ' +
                    'en voor niemand leesbaar zijn.'
                );
            }
            const docRef = doc(db, 'task_images', pathId);
            const dataData = {
                urls: urls,
                locationId,
                updatedAt: new Date().toISOString()
            };
            await setDoc(docRef, dataData, { merge: true });
        } catch (error) {
            console.error("Error saving task images:", error);
            throw error;
        }
    },

    async getTaskImages(pathId) {
        try {
            const docRef = doc(db, 'task_images', pathId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.urls && Array.isArray(data.urls)) {
                    return data.urls;
                } else if (data.url) {
                    return [data.url];
                }
            }
            return [];
        } catch (error) {
            console.error("Error getting task images:", error);
            return [];
        }
    },

    // --- STORAGE AUDIT ---
    async listAllStorageFiles(path = 'library_images/') {
        const { getStorage, ref, listAll, getMetadata } = await import('firebase/storage');
        const storage = getStorage();

        const getAllFiles = async (dirRef) => {
            let files = [];
            try {
                const res = await listAll(dirRef);

                // 1. Process files in current directory
                const filePromises = res.items.map(async (itemRef) => {
                    const meta = await getMetadata(itemRef).catch(() => ({}));
                    return {
                        name: itemRef.name,
                        fullPath: itemRef.fullPath,
                        size: meta.size,
                        timeCreated: meta.timeCreated,
                        updated: meta.updated
                    };
                });
                const currentFiles = await Promise.all(filePromises);
                files = [...files, ...currentFiles];

                // 2. Recursively process subdirectories
                const folderPromises = res.prefixes.map(folderRef => getAllFiles(folderRef));
                const subFolderFiles = await Promise.all(folderPromises);

                subFolderFiles.forEach(subFiles => {
                    files = [...files, ...subFiles];
                });

            } catch (error) {
                console.error(`Error listing files in ${dirRef.fullPath}:`, error);
            }
            return files;
        };

        const rootRef = ref(storage, path);
        return await getAllFiles(rootRef);
    },

    // --- GENERIC STORAGE OPS ---
    async deleteImage(url) {
        if (!url) return;
        try {
            const { getStorage, ref, deleteObject } = await import('firebase/storage');
            const storage = getStorage();
            const storageRef = ref(storage, url);
            await deleteObject(storageRef);
        } catch (error) {
            console.warn(`[LibraryService] Delete failed (or already gone) for ${url}:`, error);
            // Don't throw, we just want to ensure it's gone
        }
    },

    async uploadImage(file, path) {
        try {
            const { getStorage, ref, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const storage = getStorage();
            const storageRef = ref(storage, path);
            const snapshot = await uploadBytesResumable(storageRef, file);
            return await getDownloadURL(snapshot.ref);
        } catch (error) {
            console.error('[LibraryService] uploadImage failed:', error);
            throw error;
        }
    },

    // --- VERSION CONTROL ---

    /**
     * Upload a new version of a document, preserving previous versions.
     * @param {string} pathId - Document identifier (e.g., asset path ID)
     * @param {File|Blob} file - The new file to upload
     * @param {string} storagePath - Firebase Storage path prefix
     * @param {object} meta - { uploadedBy, uploadedByName, note, fileName }
     * @returns {object} { url, version }
     */
    async uploadDocumentVersion(pathId, file, storagePath, meta = {}) {
        try {
            // 1. Get existing document data
            const docRef = doc(db, 'task_images', pathId);
            const docSnap = await getDoc(docRef);
            const existingData = docSnap.exists() ? docSnap.data() : {};

            // 2. Calculate new version number
            const versionHistory = existingData.versionHistory || [];
            const newVersion = versionHistory.length + 1;
            const fileName = meta.fileName || `document_${pathId}`;

            // 3. Upload file to versioned path
            const { getStorage, ref: storageRefFn, uploadBytesResumable: uploadFn, getDownloadURL: getUrlFn } = await import('firebase/storage');
            const storage = getStorage();
            const versionedPath = `${storagePath}/v${newVersion}_${fileName}`;
            const storageRef = storageRefFn(storage, versionedPath);
            const snapshot = await uploadFn(storageRef, file);
            const downloadUrl = await getUrlFn(snapshot.ref);

            // 4. Build version entry
            const versionEntry = {
                version: newVersion,
                url: downloadUrl,
                uploadedBy: meta.uploadedBy || 'unknown',
                uploadedByName: meta.uploadedByName || 'Onbekend',
                uploadedAt: new Date().toISOString(),
                note: meta.note || '',
                fileName: fileName,
            };

            // 5. Update Firestore document
            const updatedHistory = [...versionHistory, versionEntry];
            await setDoc(docRef, {
                urls: [downloadUrl],
                updatedAt: new Date().toISOString(),
                versionHistory: updatedHistory,
                currentVersion: newVersion,
            }, { merge: true });

            console.log(`[LibraryService] Uploaded v${newVersion} of ${pathId}`);
            return { url: downloadUrl, version: newVersion };
        } catch (error) {
            console.error('[LibraryService] uploadDocumentVersion failed:', error);
            throw error;
        }
    },

    /**
     * Get full version history for a document.
     * @param {string} pathId - Document identifier
     * @returns {Array} Version history array, sorted newest first
     */
    async getDocumentHistory(pathId) {
        try {
            const docRef = doc(db, 'task_images', pathId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) return [];

            const data = docSnap.data();
            const history = data.versionHistory || [];

            // If no version history exists but there are URLs, create a synthetic v1
            if (history.length === 0 && data.urls && data.urls.length > 0) {
                return [{
                    version: 1,
                    url: data.urls[0],
                    uploadedBy: 'system',
                    uploadedByName: 'Systeem',
                    uploadedAt: data.updatedAt || new Date().toISOString(),
                    note: 'Oorspronkelijk document',
                    fileName: 'document',
                }];
            }

            // Return newest first
            return [...history].reverse();
        } catch (error) {
            console.error('[LibraryService] getDocumentHistory failed:', error);
            return [];
        }
    },

    /**
     * Revert a document to a previous version (TDO only).
     * Does NOT delete newer versions — just changes the active URL.
     * @param {string} pathId - Document identifier
     * @param {number} targetVersion - Version number to revert to
     * @returns {boolean} Success
     */
    async revertToVersion(pathId, targetVersion) {
        try {
            const docRef = doc(db, 'task_images', pathId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                throw new Error(`Document ${pathId} not found`);
            }

            const data = docSnap.data();
            const versionHistory = data.versionHistory || [];

            const targetEntry = versionHistory.find(v => v.version === targetVersion);
            if (!targetEntry) {
                throw new Error(`Version ${targetVersion} not found for ${pathId}`);
            }

            // Update the active URL to the target version
            await setDoc(docRef, {
                urls: [targetEntry.url],
                currentVersion: targetVersion,
                updatedAt: new Date().toISOString(),
                revertedFrom: data.currentVersion || versionHistory.length,
                revertedAt: new Date().toISOString(),
            }, { merge: true });

            console.log(`[LibraryService] Reverted ${pathId} to v${targetVersion}`);
            return true;
        } catch (error) {
            console.error('[LibraryService] revertToVersion failed:', error);
            throw error;
        }
    }
};
